/* eslint-disable max-lines */
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { find, isEqual, sortBy } from 'lodash-es';
import { BehaviorSubject, firstValueFrom, forkJoin, Observable } from 'rxjs';
import { distinctUntilChanged, first, map, startWith, tap } from 'rxjs/operators';

import { AddPoReportUploadComponent } from '@widgets/add-po-report-upload/add-po-report-upload.component';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { ChecklistToggleQuery } from '@models/checklist-toggle/checklist-toggle.query';

import { IconComponent } from '@shared/components/icon/icon.component';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { VarDirective } from '@shared/directives/var.directive';
import {
  EntityType,
  EventType,
  GqlService,
  listInvoicesForReconciliationQuery,
  listNotesQuery,
  NoteType,
  PermissionType,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { QuarterCloseAdjustmentsService } from '@shared/services/quarter-close-adjustments.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { UnsavedChangesHelperService } from '@shared/services/unsaved-changes-helper.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { MainQuery } from '@shared/store/main/main.query';
import { QuarterCloseNotesQuery } from '@shared/store/quarter-close-notes/quarter-close-notes.query';
import {
  DEFAULT_TASK_INSTRUCTIONS,
  QuarterCloseNotesStore,
} from '@shared/store/quarter-close-notes/quarter-close-notes.store';
import { QuarterCloseChecklistRowSections } from '@shared/store/workflow/workflow-types';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { Workflow } from '@shared/store/workflow/workflow.store';
import { parseToStartOfMonth } from '@shared/utils/parse-to-start-of-month';
import { selectWithGuardChangesModalCheck } from '@shared/utils/select-option';
import { ExportType, Utils } from '@shared/utils/utils';

import {
  ChecklistComponentChangeLockFn,
  ChecklistMonthlyDropdownDate,
  QuarterCloseChecklistPermissionList,
  QuarterCloseChecklistRowComponents,
  QuarterCloseChecklistSection,
  QuarterCloseChecklistVendorEstimateSummary,
} from './models/quarter-close-checklist.model';
import { QuarterCloseChecklistService } from './services/quarter-close-checklist.service';
import { AddVendorEstimateUploadComponent } from '../quarter-close/add-vendor-estimate-upload/add-vendor-estimate-upload.component';
import { ChecklistBannerComponent } from './components/checklist-banner/checklist-banner.component';
import { ChecklistRowInfoComponent } from './components/checklist-row-info/checklist-row-info.component';
import { ChecklistRowInfoGatherInvoicesComponent } from './components/checklist-row-info-gather-invoices/checklist-row-info-gather-invoices.component';
import { ChecklistRowTitleComponent } from './components/checklist-row-title/checklist-row-title.component';
import { calculateInvoiceMonthlyQuickView } from './utils/calculate-invoice-monthly-quick-view';
import { closeExpensesRowCompleted } from './utils/close-expenses-row-completed';
import { completedTotals } from './utils/completed-totals';
import { configureQuarterMonths } from './utils/configure-quarter-months';
import { confirmForecastSectionCompleted } from './utils/confirm-forecast-section-completed';
import { disabledLockTooltip } from './utils/disabled-lock-tooltip';
import { gatherDocumentsSectionCompleted } from './utils/gather-documents-section-completed';
import { getLockAllWorkflows } from './utils/get-lock-all-workflows';
import { isAnyWorkflowUnlockedAndHasNoPermissions } from './utils/is-any-workflow-unlocked-and-has-no-permissions';
import { isPastQuarterMonth } from './utils/is-past-quarter-month';
import { sectionCompletedTotals } from './utils/section-completed-totals';
import { sectionTotals } from './utils/section-totals';
import { workflow } from './utils/workflow';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    NgSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    NgOptionTemplateDirective,
    VarDirective,
    ExportExcelButtonComponent,
    ChecklistRowTitleComponent,
    ChecklistRowInfoComponent,
    ChecklistRowInfoGatherInvoicesComponent,
    ChecklistBannerComponent,
    AsyncPipe,
  ],
  selector: 'aux-quarter-close-checklist',
  styleUrls: ['./quarter-close-checklist.component.scss'],
  templateUrl: './quarter-close-checklist.component.html',
})
export class QuarterCloseChecklistComponent implements OnDestroy, OnInit {
  public checklistToggleQuery = inject(ChecklistToggleQuery);
  public originalWorkflowService = inject(WorkflowService);

  private authService = inject(AuthService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private checklistService = inject(QuarterCloseChecklistService);
  private readonly destroyRef = inject(DestroyRef);
  private eventService = inject(EventService);
  private globalStore = inject(GlobalStore);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private periodCloseService = inject(QuarterCloseChecklistPeriodCloseService);
  private quarterCloseAdjustmentsService = inject(QuarterCloseAdjustmentsService);
  private quarterCloseNotesQuery = inject(QuarterCloseNotesQuery);
  private quarterCloseNotesStore = inject(QuarterCloseNotesStore);
  private router = inject(Router);
  private unsavedChangesHelperService = inject(UnsavedChangesHelperService);
  private workflowQuery = inject(WorkflowQuery);

  selectedPeriodQuery = signal<Params>({});

  amountInvoiced = Utils.zeroHyphen;
  btnLoading$ = new BehaviorSubject<'export' | false>(false);
  closeExpensesRowComplete = false;
  confirmForecastSectionComplete = false;
  currentOpenMonth = '';
  readonly forecastMethodologyRoute = `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.FORECAST_METHODOLOGY}`;
  gatherDocumentsSectionComplete = false;
  isAdjustmentAvailable = false;
  isAdminUser = false;
  isClosedMonthsProcessing: boolean | null = null;
  isCurrentQuarterSelected = false;
  isLoadingList = false;
  isLockingAll = false;
  isPastQuarterMonth = false;
  isQuarterCloseEnabled = false;
  numberOfInvoices = Utils.zeroHyphen;
  permissionList: Signal<QuarterCloseChecklistPermissionList> = this.authService.getPermissions({
    userHasChecklistAdminPermission: PermissionType.PERMISSION_CHECKLIST_ADMIN,
    userHasChecklistContractsPermission: PermissionType.PERMISSION_CHECKLIST_CONTRACTS,
    userHasChecklistDiscountsPermission: PermissionType.PERMISSION_CHECKLIST_DISCOUNTS,
    userHasChecklistForecastMethodologyPermission:
      PermissionType.PERMISSION_CHECKLIST_FORECAST_METHODOLOGY,
    userHasChecklistInvoicesPermission: PermissionType.PERMISSION_CHECKLIST_INVOICES,
    userHasChecklistPatientDataPermission: PermissionType.PERMISSION_CHECKLIST_PATIENT_DATA,
    userHasChecklistPatientSiteCurvePermission:
      PermissionType.PERMISSION_CHECKLIST_PATIENT_SITE_CURVES,
    userHasChecklistTrialTimelinePermission: PermissionType.PERMISSION_CHECKLIST_TRIAL_TIMELINE,
    userHasChecklistVendorEstimatesPermission: PermissionType.PERMISSION_CHECKLIST_VENDOR_ESTIMATES,
    userHasChecklistVendorExpensesPermission: PermissionType.PERMISSION_CHECKLIST_VENDOR_EXPENSES,
  });
  protected readonly PermissionType = PermissionType;
  quarterMonths: ChecklistMonthlyDropdownDate[] = [];
  rowComponents: QuarterCloseChecklistRowComponents = this.checklistService.RowComponents;
  rows = this.checklistService.Rows;
  rowTitles = this.checklistService.RowTitles;
  sections = this.checklistService.Sections;
  sectionTitles = this.checklistService.SectionTitles;
  selectedQuarterMonth = '';
  selectedQuarterMonthFormControl = new FormControl('', {
    nonNullable: true,
  });
  @ViewChild('selectMonthEl') selectMonthEl?: NgSelectComponent;
  readonly trialTimelineRoute = `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.TIMELINE}`;
  ungroupedInvoices: listInvoicesForReconciliationQuery[] = [];
  utils = {
    disabledLockTooltip: disabledLockTooltip,
    isAnyWorkflowUnlockedAndHasNoPermissions: isAnyWorkflowUnlockedAndHasNoPermissions,
    sectionCompletedTotals: sectionCompletedTotals,
    sectionTotals: sectionTotals,
    workflow: workflow,
  };
  vendorEstimateSummaries: QuarterCloseChecklistVendorEstimateSummary[] = [];
  readonly vendorExpensesRoute = `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.EXPENSE_DETAIL}`;
  workflowList: Workflow[] = [];

  ngOnInit(): void {
    this.fetchChecklistData();

    this.selectedQuarterMonthFormControl.valueChanges
      .pipe(
        startWith(this.selectedQuarterMonthFormControl.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((selectedQuarterMonth) => {
        this.getVendorEstimateSummaries();
        this.updateSelectedPeriodQuery(selectedQuarterMonth);
      });

    this.selectedQuarterMonthFormControl.valueChanges
      .pipe(
        startWith(this.selectedQuarterMonthFormControl.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((selectedQuarterMonth) => {
        this.configureSelectedQuarterMonthChanges(selectedQuarterMonth, false);

        if (selectedQuarterMonth?.length) {
          this.quarterCloseNotesStore.update((state) => ({
            ...state,
            selectedPeriod: selectedQuarterMonth,
          }));
        }
      });
  }

  ngOnDestroy(): void {
    this.resetWorkflow();
  }

  async canDeactivate(): Promise<boolean> {
    if (this.quarterCloseNotesQuery.someNoteEditorHasUnsavedChanges()) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  changeLockStatus: ChecklistComponentChangeLockFn = (
    shouldLock: boolean,
    workflow: Workflow,
    isAssign: boolean
  ) => {
    return this.originalWorkflowService.changeLockStatus(shouldLock, workflow, isAssign);
  };

  configureDates(isCurrentQuarterSelected: boolean): void {
    this.isLoadingList = true;
    this.isCurrentQuarterSelected = isCurrentQuarterSelected;
    this.ungroupedInvoices = this.periodCloseService.ungroupedInvoices;

    if (!this.currentOpenMonth) {
      this.currentOpenMonth = this.periodCloseService.currentOpenMonth;
    }

    this.quarterMonths = configureQuarterMonths(
      this.periodCloseService.quarterMonths,
      this.currentOpenMonth
    );

    const selectedMonth = this.periodCloseService.selectedQuarterMonth;
    const selectedQuarterMonth = parseToStartOfMonth(selectedMonth);
    const persistedQuarterMonth = this.periodCloseService.getSelectedQuarterMonth(this.router.url);
    const sameMonths = selectedQuarterMonth === persistedQuarterMonth;

    if (!selectedQuarterMonth) {
      return;
    }

    this.selectedQuarterMonth = selectedQuarterMonth;

    this.updateSelectedPeriodQuery(selectedQuarterMonth);

    if (sameMonths) {
      this.selectedQuarterMonthFormControl.setValue(selectedQuarterMonth, { emitEvent: false });
      this.configureSelectedQuarterMonthChanges(selectedQuarterMonth, false);
    } else {
      this.selectedQuarterMonthFormControl.setValue(persistedQuarterMonth, { emitEvent: false });
      this.configureSelectedQuarterMonthChanges(persistedQuarterMonth, false);
    }

    this.quarterCloseNotesStore.update((state) => ({
      ...state,
      selectedPeriod: this.selectedQuarterMonthFormControl.value,
    }));
  }

  configureInvoiceData(): void {
    const invoiceQuickView = calculateInvoiceMonthlyQuickView(
      this.selectedQuarterMonth,
      this.ungroupedInvoices,
      !!this.globalStore.primaryAttribute()?.attribute_name
    );

    this.numberOfInvoices = invoiceQuickView.numberOfInvoices;
    this.amountInvoiced = invoiceQuickView.amountInvoiced;
  }

  configureSelectedQuarterMonthChanges(
    selectedQuarterMonth: null | string,
    onDestroy: boolean
  ): void {
    if (!selectedQuarterMonth) {
      this.checklistService.resetWorkflowState();

      return;
    }

    if (selectedQuarterMonth === this.selectedQuarterMonth) {
      return;
    }

    this.selectedQuarterMonth = selectedQuarterMonth;
    this.periodCloseService.selectedQuarterMonth = selectedQuarterMonth;
    this.currentOpenMonth = this.periodCloseService.currentOpenMonth;

    this.isPastQuarterMonth = isPastQuarterMonth(selectedQuarterMonth, this.currentOpenMonth);

    // Persist selected month
    if (!onDestroy) {
      this.periodCloseService.persistedQuarterMonth = selectedQuarterMonth;
    }

    this.periodCloseService.selectedQuarterMonthChanged$.next(null);

    // Pull selected month
    this.checklistService.onQuarterMonthChange(selectedQuarterMonth);
  }

  disabledWorkflowLocks(): void {
    if (!this.workflowList.length) {
      return;
    }

    this.gatherDocumentsSectionComplete = gatherDocumentsSectionCompleted(this.workflowList);
    this.confirmForecastSectionComplete = confirmForecastSectionCompleted(this.workflowList);
    this.closeExpensesRowComplete = closeExpensesRowCompleted(this.workflowList);

    this.changeDetectorRef.detectChanges();
  }

  fetchChecklistData() {
    this.checklistService
      .isAdminUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isAdminUser) => {
        this.isAdminUser = isAdminUser;
        this.changeDetectorRef.detectChanges();
      });

    this.checklistService
      .isCurrentQuarterSelected()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isCurrentQuarterSelected) => {
        this.resetWorkflow();
        this.configureDates(isCurrentQuarterSelected);
        this.changeDetectorRef.detectChanges();
      });

    this.checklistService
      .isClosedMonthsProcessing()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isClosedMonthsProcessing) => {
        this.isClosedMonthsProcessing = isClosedMonthsProcessing;
        this.changeDetectorRef.detectChanges();
      });

    this.checklistService
      .isQuarterCloseEnabled()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isQuarterCloseEnabled) => {
        this.isQuarterCloseEnabled = isQuarterCloseEnabled;
        this.changeDetectorRef.detectChanges();
      });

    this.checklistService
      .workflowAdjustmentAvailable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isAdjustmentAvailable) => {
        this.isAdjustmentAvailable = isAdjustmentAvailable;
        this.changeDetectorRef.detectChanges();

        if (!this.isAdjustmentAvailable) {
          this.workflowQuery.getAll();
        }
      });

    this.checklistService
      .workflowLoading()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isLoadingList) => {
        this.isLoadingList = isLoadingList;
        this.changeDetectorRef.detectChanges();
      });

    this.checklistService
      .workflowList()
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged(isEqual))
      .subscribe((workflowList: Workflow[]) => {
        this.workflowList = workflowList;
        this.fetchChecklistNotes();
        this.disabledWorkflowLocks();
        this.configureInvoiceData();

        this.changeDetectorRef.detectChanges();
      });
  }

  private fetchChecklistNotes(): void {
    forkJoin(
      this.quarterCloseNotesQuery.activeWorkflowList().map((workflow) =>
        this.gqlService
          .listNotes$({
            entity_id: workflow.id,
            entity_type: EntityType.WF_DETAIL,
          })
          .pipe(
            tap((response: GraphqlResponse<listNotesQuery[]>) =>
              this.saveNotesToStore(workflow, response)
            )
          )
      )
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  getVendorEstimateSummaries(): void {
    const period = this.selectedQuarterMonthFormControl.value;

    if (!period) {
      return;
    }

    const formattedPeriod = dayjs(period).format('MMM-YYYY');

    this.checklistService
      .vendorEstimateSummaries(formattedPeriod)
      .pipe(first())
      .subscribe((vendorEstimateSummaries) => {
        this.vendorEstimateSummaries = sortBy(vendorEstimateSummaries, ['name']);
        this.changeDetectorRef.detectChanges();
      });
  }

  isBtnLoading(str: string) {
    return this.btnLoading$.pipe(map((x) => x === str));
  }

  isChecklistBannerDisabled(): boolean {
    return !(
      this.gatherDocumentsSectionComplete &&
      this.confirmForecastSectionComplete &&
      this.closeExpensesRowComplete &&
      workflow(this.rowTitles.CloseDiscounts, this.workflowList)?.properties.locked
    );
  }

  isSelectedMonthOpen(): boolean {
    return dayjs(this.currentOpenMonth).isSame(dayjs(this.selectedQuarterMonth));
  }

  lockAllWorkflows = async (section: QuarterCloseChecklistSection) => {
    this.isLockingAll = true;

    const workflows = getLockAllWorkflows(section, this.workflowList);

    for (const workflow of workflows) {
      await this.changeLockStatus(true, workflow, false);
    }

    this.isLockingAll = false;
  };

  onAddPoReportUploadClick: () => void = () => {
    this.overlayService.openPopup({
      content: AddPoReportUploadComponent,
      settings: {
        header: 'Upload PO Report',
        primaryButton: {
          action: (instance: AddPoReportUploadComponent | null) => instance?.onUpload(),
          disabled: (instance: AddPoReportUploadComponent | null) =>
            !!instance?.isUploadButtonDisabled(),
          label: 'Save',
          pendoTag: 'upload-po-report-button',
          tooltip: (instance: AddPoReportUploadComponent | null) =>
            instance ? instance?.uploadButtonTooltip() : '',
        },
      },
    });
  };

  onAddVendorEstimateUploadClick: () => void = async () => {
    const ref = this.overlayService.openPopup<
      undefined,
      { goToEditModeWithCurrentFilters?: boolean; month?: string; vendorId?: string },
      AddVendorEstimateUploadComponent
    >({
      content: AddVendorEstimateUploadComponent,
      settings: {
        header: 'Send Vendor Estimate Support to Auxilius',
        primaryButton: {
          action: (instance) => instance?.onUpload(),
          label: 'Send Vendor Estimate Support',
        },
      },
    });

    const resp = await firstValueFrom(ref.afterClosed$);

    this.getVendorEstimateSummaries();

    const { goToEditModeWithCurrentFilters, month, vendorId } = resp.data || {};

    if (vendorId || goToEditModeWithCurrentFilters) {
      if (vendorId) {
        this.quarterCloseAdjustmentsService.updateFormControlValues('', [vendorId]);
      }

      const filterMonth = goToEditModeWithCurrentFilters ? this.currentOpenMonth : month;

      const editQueryParam =
        dayjs(month).format('YYYY-MM') ===
        dayjs(this.mainQuery.getValue().currentOpenMonth).format('YYYY-MM')
          ? {
              editMode: true,
            }
          : {};

      void this.router.navigate(
        [`/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.EXPENSE_DETAIL}`],
        {
          queryParams: {
            ...editQueryParam,
            currentOpenMonth: dayjs(filterMonth).add(2, 'day').format('YYYY-MM-DD'),
            vendorId: vendorId ?? null,
          },
        }
      );
    }
  };

  onExportChecklist = async () => {
    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('export');

    const date = dayjs(this.periodCloseService.getSelectedQuarterMonth(this.router.url));
    const isDateValid = date.isValid();
    const dateStr = isDateValid ? date.format('MMM-YYYY') : '';

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_entity_id: 'WF_MONTH_CLOSE_LOCK',
          export_type: ExportType.CLOSING_PERIOD_CHECKLIST,
          filename: `${dateStr}_closing-period-checklist`,
          json_properties: { month: dateStr },
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }

    this.btnLoading$.next(false);
  };

  quarterCloseChecklistTitle$(workflowList: Workflow[]): Observable<string> {
    return this.periodCloseService.isCurrentQuarterSelected.pipe(
      first(),
      map(() => {
        const workflowLength = this.workflowList.filter(
          (workflow) => workflow.section !== QuarterCloseChecklistRowSections['Close Month']
        ).length;
        const date = dayjs(this.periodCloseService.getSelectedQuarterMonth(this.router.url));
        const isDateValid = date.isValid();
        const dateStr = isDateValid ? date.format('MMMM YYYY') : '';

        return `${dateStr} - Closing Checklist (${completedTotals(
          workflowList
        )}/${workflowLength} Complete)`;
      })
    );
  }

  resetWorkflow(): void {
    const latestMonth = this.periodCloseService.getLatestMonth();

    this.configureSelectedQuarterMonthChanges(latestMonth, true);
  }

  reviewVendorEstimateOpened() {
    this.getVendorEstimateSummaries();
  }

  private saveNotesToStore(workflow: Workflow, response: GraphqlResponse<listNotesQuery[]>): void {
    const instruction = find(
      response?.data,
      (listNotesQuery) => listNotesQuery.note_type === NoteType.NOTE_TYPE_TASK_INSTRUCTIONS
    );

    const monthlyReviewNotes =
      response?.data?.filter(
        (listNotesQuery) => listNotesQuery.note_type === NoteType.NOTE_TYPE_MONTHLY_REVIEW_NOTE
      ) || [];

    this.quarterCloseNotesStore.updateTaskInstruction(workflow.id, {
      id: instruction?.id,
      isDefault: !instruction?.message,
      text:
        instruction?.message ||
        DEFAULT_TASK_INSTRUCTIONS[workflow.step_type as keyof typeof DEFAULT_TASK_INSTRUCTIONS]
          .text,
      updated: instruction?.update_date ? instruction.update_date : undefined,
    });

    this.quarterCloseNotesStore.updateMonthlyReviewNotes(
      workflow.id,
      monthlyReviewNotes.map((note) => ({
        created: note.create_date,
        createdBy: note.created_by,
        id: note.id,
        text: note.message,
        workflowId: note.entity_id,
      }))
    );
  }

  async selectMonth(event: MouseEvent, value: string, disabled: boolean): Promise<void> {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();

      return;
    }

    await selectWithGuardChangesModalCheck({
      element: this.selectMonthEl,
      event,
      formControl: this.selectedQuarterMonthFormControl,
      hasUnsavedChanges: this.unsavedChangesHelperService.getUnsavedChangesDecisionByRouterUrl(),
      overlayService: this.overlayService,
      value,
    });
  }

  updateSelectedPeriodQuery(selectedQuarterMonth: string): void {
    const date = dayjs(selectedQuarterMonth);

    this.selectedPeriodQuery.set({
      endDate: date.endOf('month').format('YYYY-MM-DD'),
      startDate: date.startOf('month').format('YYYY-MM-DD'),
    });
  }
}
